import React, { FC, useContext, useState } from 'react';
import styled from 'styled-components';
import { Button, Col, Form, Row } from 'antd';
import { FormComponentProps } from 'antd/lib/form';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import AntDesignStyleOverrides from '../../core/components/App/AntDesignStyleOverrides';
import { InputLocation } from './InputLocation';
import { Organization } from '../hooks/useGetConsumerSchedulingOrganizationQuery';
import { SelectPayorAndPlan } from './SelectPayorAndPlan';
import { SelectSpecialtyAndProcedureForOrganization } from './SelectSpecialtyAndProcedureForOrganization';
import { ConsumerSchedulingContext } from '../contexts/ConsumerSchedulingContext';

export interface SearchFormData {
  location: string;
  payorId?: string;
  payorPlanId?: string;
  procedureId: string;
  specialtyId: string;
}

interface Props extends FormComponentProps {
  form: WrappedFormUtils<SearchFormData>;
  initialLocation?: string;
  initialPayorId?: string;
  initialPayorPlanId?: string;
  initialProcedureId?: string;
  initialSpecialtyId?: string;
  onSubmit: (values: SearchFormData) => void;
  organization: Organization;
  setLatitude: (latitude: number | undefined) => void;
  setLongitude: (longitude: number | undefined) => void;
  embedded: boolean;
}

const Styles = styled.div`
  .search-button {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    font-family: Source Sans Pro, sans-serif;
    font-size: 14px;
    height: 38px;
    text-align: center;
    text-decoration: none solid;
    text-transform: uppercase;
    width: 100%;
  }
`;

const SearchFormComponent: FC<Props> = ({
  form,
  initialLocation,
  initialPayorId,
  initialPayorPlanId,
  initialProcedureId,
  initialSpecialtyId,
  onSubmit,
  setLatitude,
  setLongitude,
  embedded,
}): JSX.Element => {
  const { organization } = useContext(ConsumerSchedulingContext);
  const [disabled, setDisabled] = useState<boolean>(false);

  const handleSearchFormSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        onSubmit(values);
      }
    });
  };

  return (
    <AntDesignStyleOverrides>
      <Styles>
        <Form onSubmit={handleSearchFormSubmit}>
          <Row gutter={[24, 0]}>
            <Col xs={24} lg={8}>
              <SelectSpecialtyAndProcedureForOrganization
                form={form}
                initialProcedureId={initialProcedureId}
                initialSpecialtyId={initialSpecialtyId}
              />
            </Col>
            {organization.consumerSchedulingSettings === null || organization.consumerSchedulingSettings?.showMap ? (
              <Col xs={24} lg={8}>
                <InputLocation
                  form={form}
                  initialLocation={initialLocation}
                  setLatitude={setLatitude}
                  setLongitude={setLongitude}
                  embedded={embedded}
                  onLocationChange={valid => setDisabled(!valid)}
                />
              </Col>
            ) : null}
            <Col xs={24} lg={8}>
              {organization.consumerSchedulingSettings === null ||
              organization.consumerSchedulingSettings?.searchInsurance ? (
                <SelectPayorAndPlan
                  form={form}
                  initialPayorId={initialPayorId}
                  initialPayorPlanId={initialPayorPlanId}
                />
              ) : null}
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Button className="search-button" type="primary" htmlType="submit" disabled={disabled}>
                Search
              </Button>
            </Col>
          </Row>
        </Form>
      </Styles>
    </AntDesignStyleOverrides>
  );
};

export const SearchForm = Form.create<Props>()(SearchFormComponent);
